import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Page, Card, Banner, BlockStack, InlineStack, Text, Button, DropZone, Badge, Box, Collapsible, ChoiceList, Divider, Modal, } from "@shopify/polaris";
import Papa from "papaparse";
import { CSV_COLUMNS, computeDiff } from "./diff";
import { apiFetch } from "./api";
export default function App() {
    const [status, setStatus] = useState("idle");
    const [shopName, setShopName] = useState("");
    const [error, setError] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [csvRows, setCsvRows] = useState(null);
    const [csvFileName, setCsvFileName] = useState("");
    const [csvError, setCsvError] = useState("");
    const [diffLoading, setDiffLoading] = useState(false);
    const [diff, setDiff] = useState(null);
    const [mode, setMode] = useState("incremental");
    const [expanded, setExpanded] = useState({});
    async function checkShop() {
        setStatus("loading");
        setError("");
        try {
            const resp = await apiFetch(`/shop`);
            const body = await resp.json();
            if (!resp.ok)
                throw new Error(body.error || `HTTP ${resp.status}`);
            setShopName(body.shop.name);
            setStatus("ok");
        }
        catch (e) {
            setError(String(e));
            setStatus("err");
        }
    }
    async function downloadExport() {
        setExporting(true);
        setExportError("");
        try {
            const resp = await apiFetch(`/export`);
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
            const blob = await resp.blob();
            const cd = resp.headers.get("content-disposition") || "";
            const m = cd.match(/filename="?([^"]+)"?/);
            const filename = m ? m[1] : `store_locations_${new Date().toISOString().slice(0, 10)}.csv`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        }
        catch (e) {
            setExportError(String(e));
        }
        finally {
            setExporting(false);
        }
    }
    function handleDrop(_files, accepted) {
        setCsvError("");
        setDiff(null);
        // ApplyCard remounts on new file → its summary state is intentionally reset here
        const f = accepted[0];
        if (!f)
            return;
        setCsvFileName(f.name);
        Papa.parse(f, {
            header: true,
            skipEmptyLines: true,
            complete: async (res) => {
                if (res.errors.length) {
                    setCsvError(`CSV parse error: ${res.errors[0].message} (row ${res.errors[0].row})`);
                    return;
                }
                const missingCols = CSV_COLUMNS.filter(c => !(res.meta.fields || []).includes(c));
                if (missingCols.length) {
                    setCsvError(`CSV is missing required column(s): ${missingCols.join(", ")}`);
                    return;
                }
                const rows = res.data.map(r => {
                    const out = {};
                    for (const c of CSV_COLUMNS)
                        out[c] = r[c] ?? "";
                    return out;
                });
                setCsvRows(rows);
                await refreshDiff(rows);
            },
            error: (err) => setCsvError(String(err)),
        });
    }
    async function refreshDiff(rows) {
        setDiffLoading(true);
        try {
            const resp = await apiFetch(`/list`);
            if (!resp.ok)
                throw new Error(`Worker /list HTTP ${resp.status}`);
            const body = await resp.json();
            const d = computeDiff(rows, body.rows);
            setDiff(d);
        }
        catch (e) {
            setCsvError(String(e));
        }
        finally {
            setDiffLoading(false);
        }
    }
    function reset() {
        setCsvRows(null);
        setCsvFileName("");
        setCsvError("");
        setDiff(null);
        setExpanded({});
    }
    useEffect(() => { checkShop(); }, []);
    const fileLabel = csvFileName ? `Loaded: ${csvFileName} — ${csvRows?.length ?? 0} rows` : "Drop a CSV file here or click to choose";
    return (_jsx(Page, { title: "ONA Store Importer", subtitle: "Bulk import store locations from CSV", children: _jsxs(BlockStack, { gap: "400", children: [status === "ok" && (_jsx(Banner, { tone: "success", title: "Worker connected", children: _jsxs("p", { children: ["Talking to ", _jsx(Text, { as: "span", fontWeight: "bold", children: shopName }), " via Cloudflare Worker."] }) })), status === "err" && (_jsxs(Banner, { tone: "critical", title: "Worker check failed", children: [_jsx("p", { children: error }), _jsx(Button, { onClick: checkShop, children: "Retry" })] })), _jsx(Card, { children: _jsxs(BlockStack, { gap: "300", children: [_jsx(Text, { as: "h2", variant: "headingMd", children: "1 \u00B7 Export current data" }), _jsxs(Text, { as: "p", tone: "subdued", children: ["Download all ", _jsx("code", { children: "store_location" }), " metaobjects as CSV \u2014 the", _jsx(Text, { as: "span", fontWeight: "bold", children: " id" }), " column is the matching key for re-import."] }), exportError && _jsx(Banner, { tone: "critical", children: exportError }), _jsx(InlineStack, { children: _jsx(Button, { loading: exporting, disabled: status !== "ok", onClick: downloadExport, children: "Download CSV" }) })] }) }), _jsx(Card, { children: _jsxs(BlockStack, { gap: "300", children: [_jsx(Text, { as: "h2", variant: "headingMd", children: "2 \u00B7 Upload edited CSV" }), _jsx(DropZone, { accept: ".csv,text/csv", allowMultiple: false, onDrop: handleDrop, children: _jsx(DropZone.FileUpload, { actionTitle: fileLabel }) }), csvError && _jsx(Banner, { tone: "critical", children: csvError }), csvRows && !csvError && _jsx(InlineStack, { children: _jsx(Button, { onClick: reset, children: "Choose a different file" }) })] }) }), (diffLoading || diff) && (_jsx(Card, { children: _jsxs(BlockStack, { gap: "400", children: [_jsx(Text, { as: "h2", variant: "headingMd", children: "3 \u00B7 Preview changes" }), diffLoading && _jsx(Text, { as: "p", tone: "subdued", children: "Comparing against current data\u2026" }), diff && _jsx(DiffSummary, { diff: diff, mode: mode, setMode: setMode, expanded: expanded, setExpanded: setExpanded })] }) })), diff && _jsx(ApplyCard, { csvRows: csvRows, diff: diff, mode: mode, onDone: () => refreshDiff(csvRows) })] }) }));
}
function ApplyCard({ csvRows, diff, mode, onDone }) {
    const [confirming, setConfirming] = useState(false);
    const [applying, setApplying] = useState(false);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState("");
    const invalidCount = diff.invalid.length;
    const willCreate = diff.create.length;
    const willUpdate = diff.update.length;
    const willDelete = mode === "replace" ? diff.missing.length : 0;
    const blocked = invalidCount > 0;
    const nothingToDo = willCreate === 0 && willUpdate === 0 && willDelete === 0;
    async function applyNow() {
        setApplying(true);
        setError("");
        setSummary(null);
        setConfirming(false);
        try {
            // Only send rows that need writing. For replace mode we also tell the
            // server which existing handles to PRESERVE (unchanged + invalid) so it
            // doesn't sweep them when deleting "missing" rows.
            const rowsToWrite = [
                ...diff.create.map(c => c.csv),
                ...diff.update.map(c => c.csv),
            ];
            const keepIds = [
                ...diff.unchanged.map(c => c.csv.id),
                ...diff.invalid.map(p => p.csv.id),
            ].filter(Boolean);
            const resp = await apiFetch(`/import`, {
                method: "POST",
                body: JSON.stringify({ rows: rowsToWrite, mode, keepIds }),
            });
            const body = await resp.json();
            if (!resp.ok)
                throw new Error(body.error || `HTTP ${resp.status}`);
            setSummary(body);
            onDone();
        }
        catch (e) {
            setError(String(e));
        }
        finally {
            setApplying(false);
        }
    }
    return (_jsxs(Card, { children: [_jsxs(BlockStack, { gap: "300", children: [_jsx(Text, { as: "h2", variant: "headingMd", children: "4 \u00B7 Apply" }), blocked && _jsxs(Banner, { tone: "warning", children: ["Fix the ", invalidCount, " invalid row(s) before importing."] }), nothingToDo && !blocked && _jsx(Banner, { tone: "info", children: "Nothing to do \u2014 no creates, updates, or deletes." }), error && _jsx(Banner, { tone: "critical", children: error }), summary && (_jsxs(Banner, { tone: summary.failed > 0 ? "warning" : "success", children: [_jsxs("p", { children: [_jsx(Text, { as: "span", fontWeight: "bold", children: "Import complete." }), " ", "Created ", summary.created, " \u00B7 Updated ", summary.updated, " \u00B7 Deleted ", summary.deleted, " \u00B7 Failed ", summary.failed] }), summary.failed > 0 && (_jsx(BlockStack, { gap: "100", children: summary.results.filter(r => !r.ok).slice(0, 10).map((r, i) => (_jsxs(Text, { as: "p", tone: "subdued", children: [r.action, " \u00B7 ", r.title || r.id || "(no title)", " \u2014 ", r.error] }, i))) }))] })), _jsx(InlineStack, { children: _jsx(Button, { variant: "primary", tone: mode === "replace" ? "critical" : undefined, disabled: blocked || nothingToDo || applying, loading: applying, onClick: () => (mode === "replace" ? setConfirming(true) : applyNow()), children: mode === "replace"
                                ? `Replace all — create ${willCreate}, update ${willUpdate}, delete ${willDelete}`
                                : `Apply — create ${willCreate}, update ${willUpdate}` }) })] }), _jsx(Modal, { open: confirming, onClose: () => setConfirming(false), title: "Confirm full replace", primaryAction: { content: `Yes, delete ${willDelete} and apply`, destructive: true, onAction: applyNow }, secondaryActions: [{ content: "Cancel", onAction: () => setConfirming(false) }], children: _jsx(Modal.Section, { children: _jsxs(BlockStack, { gap: "200", children: [_jsxs(Text, { as: "p", children: ["This will ", _jsxs(Text, { as: "span", fontWeight: "bold", children: ["permanently delete ", willDelete, " store(s)"] }), " currently in Shopify that are not present in your CSV, plus create ", willCreate, " and update ", willUpdate, "."] }), _jsx(Text, { as: "p", tone: "subdued", children: "This cannot be undone except by re-importing the deleted rows." })] }) }) })] }));
}
function DiffSummary({ diff, mode, setMode, expanded, setExpanded, }) {
    const total = diff.create.length + diff.update.length + diff.unchanged.length;
    return (_jsxs(BlockStack, { gap: "400", children: [_jsxs(InlineStack, { gap: "300", wrap: true, children: [_jsx(Stat, { label: "Create", tone: "success", count: diff.create.length, k: "create", expanded: expanded, setExpanded: setExpanded }), _jsx(Stat, { label: "Update", tone: "attention", count: diff.update.length, k: "update", expanded: expanded, setExpanded: setExpanded }), _jsx(Stat, { label: "Unchanged", tone: "info", count: diff.unchanged.length, k: "unchanged", expanded: expanded, setExpanded: setExpanded }), _jsx(Stat, { label: mode === "replace" ? "Delete" : "Missing from CSV", tone: mode === "replace" ? "critical" : "info", count: diff.missing.length, k: "missing", expanded: expanded, setExpanded: setExpanded }), _jsx(Stat, { label: "Invalid", tone: "critical", count: diff.invalid.length, k: "invalid", expanded: expanded, setExpanded: setExpanded })] }), _jsx(Divider, {}), _jsx(ChoiceList, { title: "Apply mode", choices: [
                    { label: `Incremental — create new, update changed, keep existing rows that aren't in the CSV (${diff.missing.length} rows)`, value: "incremental" },
                    { label: `Full replace — also delete existing rows not in the CSV (${diff.missing.length} rows)`, value: "replace" },
                ], selected: [mode], onChange: vals => setMode(vals[0] || "incremental") }), _jsx(Box, { children: _jsxs(Text, { as: "p", tone: "subdued", children: [total, " CSV rows \u00B7 ", diff.invalid.length > 0 ? "fix invalid rows before importing." : "ready to import."] }) }), _jsx(Collapsible, { open: !!expanded.create, id: "create", children: _jsx(RowList, { title: "Create", rows: diff.create.map(c => c.csv) }) }), _jsx(Collapsible, { open: !!expanded.update, id: "update", children: _jsx(UpdateList, { rows: diff.update }) }), _jsx(Collapsible, { open: !!expanded.unchanged, id: "unchanged", children: _jsx(RowList, { title: "Unchanged", rows: diff.unchanged.map(c => c.csv) }) }), _jsx(Collapsible, { open: !!expanded.missing, id: "missing", children: _jsx(RowList, { title: mode === "replace" ? "Will be deleted" : "Currently in Shopify, not in CSV", rows: diff.missing }) }), _jsx(Collapsible, { open: !!expanded.invalid, id: "invalid", children: _jsx(BlockStack, { gap: "100", children: diff.invalid.map((p, i) => (_jsxs(Box, { padding: "200", background: "bg-surface-secondary", borderRadius: "200", children: [_jsxs(Text, { as: "p", fontWeight: "semibold", children: [p.csv.title || "(no title)", " \u2014 ", p.csv.id || "(no id)"] }), _jsx(Text, { as: "p", tone: "critical", children: p.reasons.join("; ") })] }, i))) }) })] }));
}
function Stat({ label, tone, count, k, expanded, setExpanded }) {
    const onClick = () => setExpanded({ ...expanded, [k]: !expanded[k] });
    const isOpen = !!expanded[k];
    return (_jsxs(InlineStack, { gap: "200", blockAlign: "center", children: [_jsx(Badge, { tone: tone, children: String(count) }), _jsx(Button, { variant: "plain", onClick: onClick, disabled: count === 0, disclosure: isOpen ? "up" : "down", children: label })] }));
}
function RowList({ title, rows }) {
    if (rows.length === 0)
        return null;
    return (_jsxs(BlockStack, { gap: "100", children: [_jsxs(Text, { as: "p", tone: "subdued", children: [title, " \u00B7 ", rows.length] }), rows.slice(0, 200).map((r, i) => (_jsxs(Box, { padding: "200", background: "bg-surface-secondary", borderRadius: "200", children: [_jsx(Text, { as: "p", fontWeight: "semibold", children: r.title || "(no title)" }), _jsxs(Text, { as: "p", tone: "subdued", children: [r.id || "(new)", " \u00B7 ", r.state || "—", " \u00B7 ", r.suburb || ""] })] }, i))), rows.length > 200 && _jsxs(Text, { as: "p", tone: "subdued", children: ["\u2026 and ", rows.length - 200, " more"] })] }));
}
function UpdateList({ rows }) {
    if (rows.length === 0)
        return null;
    return (_jsxs(BlockStack, { gap: "100", children: [rows.slice(0, 200).map((row, i) => (_jsxs(Box, { padding: "200", background: "bg-surface-secondary", borderRadius: "200", children: [_jsx(Text, { as: "p", fontWeight: "semibold", children: row.csv.title || "(no title)" }), _jsxs(Text, { as: "p", tone: "subdued", children: [row.csv.id, " \u00B7 ", row.changedKeys.length, " field(s) changed: ", row.changedKeys.join(", ")] })] }, i))), rows.length > 200 && _jsxs(Text, { as: "p", tone: "subdued", children: ["\u2026 and ", rows.length - 200, " more"] })] }));
}
